/* oxlint-disable */
'use client';

import {
  JSXElementConstructor, MouseEvent, ReactElement, ReactNode, ReactPortal, useLayoutEffect, useState, useEffect, useRef,
} from 'react';

import { StaticImport } from 'next/dist/shared/lib/get-img-props';
import Image from 'next/image';

import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';

import { MdKeyboardDoubleArrowLeft, MdKeyboardDoubleArrowRight } from 'react-icons/md';

import styled from 'styled-components';
import { UrlObject } from 'url';

import { v4 as uuidv4 } from 'uuid';

import { core } from '@/features/v2/common/privaMenu/localization/core';
import { routes } from '@/features/v2/constants/routes';
import { useIntercom } from '@/features/v2/shared/ContactSection/hooks/useIntercom';
import { userUIStore } from '@/stores/userUIStore';
import { isSportsbookPath, navigateBetbyHome } from '@/utils/betbyNavigation';

import { MisionesSheet } from '@/components/sheet/MisionesSheet';
import { ComingSoonBadge } from '@/components/ui/comingSoonBadge';

const missionBoardName = process.env.NEXT_PUBLIC_SOLITICS_MISSION_BOARD_NAME ?? 'Dark Mission Board - Spanish';

const isDepositoPath = (path: string | null) => {
  return path != null && path.startsWith('/deposito');
};

const StyledDiv = styled.div`
  display: none;
  @media (min-width: 900px) {
    display: block;
  }
`;

const SideBar = () => {
  const { isSidebarOpen, toggleSidebar, setIsSidebarOpen } = userUIStore();
  const { handleSupportClick } = useIntercom();
  const router = useRouter();
  const pathname = usePathname();
  const [isMobile, setIsMobile] = useState(false);
  const prevPathnameRef = useRef<string | null>(null);

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    if (isDepositoPath(pathname)) return;
    if (window.innerWidth >= 1200 && !isSidebarOpen) {
      toggleSidebar();
    }
    // Solo apertura inicial en desktop fuera de /deposito; no re-ejecutar al navegar.
  }, []);

  useLayoutEffect(() => {
    const current = pathname ?? '';
    const previous = prevPathnameRef.current;
    const cameFromOutside = previous === null || !isDepositoPath(previous);
    if (isDepositoPath(current) && cameFromOutside) {
      setIsSidebarOpen(false);
    }
    prevPathnameRef.current = current;
  }, [pathname, setIsSidebarOpen]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1200);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  return (
    <StyledDiv>
      {!isSidebarOpen && isMobile && (
        <div
          className="fixed inset-0 bg-transparent z-40"
          onClick={toggleSidebar}
        />
      )}

      <aside className={`
                fixed top-20 left-0 h-full rounded-b-xl transition-all  duration-300 ease-in-out z-50
                ${isSidebarOpen ? 'w-[210px]' : 'w-16'}
                md:translate-x-0
            `}
      >
        <div className="flex flex-col h-full  rounded-br-xl relative ">

          <div className={`flex-1 py-1 bg-default-50 mt-5 rounded-xl  ${isSidebarOpen ? 'px-2' : 'px-1'}`}>
            <MisionesSheet missionBoardName={missionBoardName} />
            <div className="flex justify-center items-center">
              <div className="h-1 w-full me-3 bg-white/10 rounded-full mt-2" />
              {isSidebarOpen ? (
                <div className="absolute mt-2 -right-4">
                  <button
                    type="button"
                    className="rounded-full bg-white/20 p-0.5"
                    onClick={toggleSidebar}
                  >
                    <MdKeyboardDoubleArrowLeft className="h-7 w-7" />
                  </button>
                </div>
              ) : (
                <div className="absolute mt-2 -right-4">
                  <button
                    type="button"
                    className="rounded-full bg-white/10 p-0.5"
                    onClick={toggleSidebar}
                  >
                    <MdKeyboardDoubleArrowRight className="h-7 w-7" />
                  </button>
                </div>
              )}
            </div>
            <div className="flex flex-col gap-5 justify-center items-center mt-5">
              {core.menu.map((item: { url: string | UrlObject; img: string | StaticImport; title: string | number | bigint | boolean | ReactElement<any, string | JSXElementConstructor<any>> | Iterable<ReactNode> | ReactPortal | Promise<any> | Iterable<ReactNode> | null | undefined; comingSoon?: boolean; }) => {
                if (item.title === 'Chat en vivo') {
                  return (
                    <button
                      type="button"
                      onClick={handleSupportClick}
                      key={uuidv4()}
                      className={`${isSidebarOpen ? 'justify-start' : 'justify-center'} flex items-center gap-2 w-full p-2 hover-border-bottom rounded-md relative ${item.comingSoon ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''}`}
                    >
                      <Image
                        src={item.img}
                        alt={item.title as string}
                        width={30}
                        height={30}
                        className={`h-[30px] w-[30px] object-contain rounded-md ${item.comingSoon ? 'grayscale' : ''}`}
                      />
                      {isSidebarOpen && (
                        <p className={`text-center text-xs ${item.comingSoon ? 'text-white/40' : 'text-white'}`}>{item.title}</p>
                      )}
                      {item.comingSoon && isSidebarOpen && (
                        <ComingSoonBadge className="absolute -top-1 -right-1" />
                      )}
                    </button>
                  );
                }

                if (item.title === 'Ayuda / Faq') {
                  const handleFaqClick = () => {
                    const scrollToBottom = () => {
                      window.scrollTo({
                        top: document.documentElement.scrollHeight,
                        behavior: 'smooth',
                      });
                    };

                    if (pathname === '/') {
                      scrollToBottom();
                    } else {
                      router.push('/');
                      setTimeout(scrollToBottom, 300);
                    }
                  };

                  return (
                    <button
                      type="button"
                      onClick={handleFaqClick}
                      key={uuidv4()}
                      className={`${isSidebarOpen ? 'justify-start' : 'justify-center'} flex items-center gap-2 w-full p-2 hover-border-bottom rounded-md relative ${item.comingSoon ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''}`}
                    >
                      <Image
                        src={item.img}
                        alt={item.title as string}
                        width={30}
                        height={30}
                        className={`h-[30px] w-[30px] object-contain rounded-md ${item.comingSoon ? 'grayscale' : ''}`}
                      />
                      {isSidebarOpen && (
                        <p className={`text-center text-xs ${item.comingSoon ? 'text-white/40' : 'text-white'}`}>{item.title}</p>
                      )}
                      {item.comingSoon && isSidebarOpen && (
                        <ComingSoonBadge className="absolute -top-1 -right-1" />
                      )}
                    </button>
                  );
                }

                if (item.url.toString().includes('https://')) {
                  return (
                    <Link
                      prefetch={false}
                      target="_blank"
                      key={uuidv4()}
                      href={item.url}
                      className={`${isSidebarOpen ? 'justify-start' : 'justify-center'} flex items-center gap-2 w-full p-2 hover-border-bottom rounded-md relative ${item.comingSoon ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''}`}
                    >
                      <Image
                        src={item.img}
                        alt={item.title as string}
                        width={30}
                        height={30}
                        className={`h-[30px] w-[30px] object-contain rounded-md ${item.comingSoon ? 'grayscale' : ''}`}
                      />
                      {isSidebarOpen && (
                        <p className={`text-center text-xs ${item.comingSoon ? 'text-white/40' : 'text-white'}`}>{item.title}</p>
                      )}
                      {item.comingSoon && isSidebarOpen && (
                        <ComingSoonBadge className="absolute -top-1 -right-1" />
                      )}
                    </Link>
                  );
                }

                const itemUrl = typeof item.url === 'string' ? item.url : item.url.pathname || '';
                const handleSportsbookClick = (event: MouseEvent<HTMLAnchorElement>) => {
                  if (itemUrl !== routes.SPORTS || !isSportsbookPath(pathname)) {
                    return;
                  }

                  if (navigateBetbyHome()) {
                    event.preventDefault();
                  }
                };

                return (
                  <Link
                    prefetch={false}
                    key={uuidv4()}
                    href={item.url}
                    onClick={handleSportsbookClick}
                    className={`${isSidebarOpen ? 'justify-start' : 'justify-center'} flex items-center gap-2 w-full p-2 hover-border-bottom rounded-md relative ${item.comingSoon ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''}`}
                  >
                    <Image
                      src={item.img}
                      alt={item.title as string}
                      width={30}
                      height={30}
                      className={`h-[30px] w-[30px] object-contain rounded-md ${item.comingSoon ? 'grayscale' : ''}`}
                    />
                    {isSidebarOpen && (
                      <p className={`text-center text-xs ${item.comingSoon ? 'text-white/40' : 'text-white'}`}>{item.title}</p>
                    )}
                    {item.comingSoon && isSidebarOpen && (
                      <ComingSoonBadge className="absolute -top-1 -right-1" />
                    )}
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </aside>
    </StyledDiv>
  );
};

export default SideBar;
